import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import DecimalInput from './UiComponents/DecimalInput';
import RatingService from './RatingService';
import TariffSelector from './TariffSelector';
import TariffService from './TariffService';
import './ElectricityBillCalculatorPage.css';

const PAGE_URL = 'https://suzmesayac.com/elektrik-faturasi-hesaplama';

const money = (value) => new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}).format(Number(value || 0));

const quantity = (value, digits = 3) => new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: digits
}).format(Number(value || 0));

const taxLabel = (taxId) => {
  if (taxId?.startsWith('KDV')) return `KDV (%${taxId.replace('KDV', '')})`;
  if (taxId?.startsWith('BTV')) return `Belediye Tüketim Vergisi (%${taxId.replace('BTV', '')})`;
  return taxId;
};

const getErrorMessage = (error) => (
  error?.response?.data?.message ||
  error?.response?.data?.detail ||
  'Fatura hesaplanamadı. Bilgileri kontrol edip yeniden deneyin.'
);

const SeoHead = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const definitions = {
      description: 'Elektrik faturası hesaplama aracıyla güncel tarifelere göre mesken, sanayi, tarımsal ve diğer abone gruplarının tahmini faturasını; enerji, dağıtım, kademe ve vergiler dahil hesaplayın.',
      robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
    };
    const previousMeta = {};

    document.title = 'Elektrik Faturası Hesaplama 2026 | Güncel Tarifeler';
    Object.entries(definitions).forEach(([name, content]) => {
      let element = document.head.querySelector(`meta[name="${name}"]`);
      previousMeta[name] = element?.getAttribute('content') ?? null;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    });

    const canonical = document.head.querySelector('link[rel="canonical"]');
    const previousCanonical = canonical?.getAttribute('href') ?? null;
    const canonicalElement = canonical || document.createElement('link');
    canonicalElement.setAttribute('rel', 'canonical');
    canonicalElement.setAttribute('href', PAGE_URL);
    if (!canonical) document.head.appendChild(canonicalElement);

    const socialMeta = [
      ['property', 'og:title', 'Elektrik Faturası Hesaplama 2026'],
      ['property', 'og:description', 'Güncel sübvansiyonlu ve sübvansiyonsuz tarifelerle ayrıntılı elektrik faturası hesaplayın.'],
      ['property', 'og:url', PAGE_URL],
      ['name', 'twitter:title', 'Elektrik Faturası Hesaplama 2026'],
      ['name', 'twitter:description', 'Tarife, tüketim, dağıtım ve vergi ayrıntılarıyla elektrik faturası hesaplama aracı.']
    ].map(([attribute, key, content]) => {
      const selector = `meta[${attribute}="${key}"]`;
      let element = document.head.querySelector(selector);
      const previous = element?.getAttribute('content') ?? null;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
      return { element, previous, created: previous === null };
    });

    const structuredData = document.createElement('script');
    structuredData.id = 'electricity-calculator-structured-data';
    structuredData.type = 'application/ld+json';
    structuredData.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebApplication',
          name: 'Elektrik Faturası Hesaplama',
          url: PAGE_URL,
          applicationCategory: 'UtilitiesApplication',
          operatingSystem: 'Web',
          inLanguage: 'tr',
          isAccessibleForFree: true,
          description: 'Güncel elektrik tarifelerine göre enerji, dağıtım ve vergiler dahil ayrıntılı fatura hesaplama aracı.',
          featureList: [
            'Sübvansiyonlu ve sübvansiyonsuz tarife desteği',
            'Tek ve çok zamanlı tarife hesaplama',
            'Kademe ve birim fiyat dökümü',
            'Vergi ve dağıtım bedeli ayrıntıları'
          ]
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            ['Elektrik faturası nasıl hesaplanır?', 'Okuma dönemindeki kWh tüketimi seçilen tarifenin enerji ve dağıtım birim fiyatlarıyla değerlendirilir; ilgili vergi ve diğer tarife kalemleri eklenerek tahmini toplam bulunur.'],
            ['Sübvansiyonsuz elektrik faturası hesaplanabilir mi?', 'Evet. Araç hem sübvansiyon uygulanan ulusal tarifeleri hem de sübvansiyon uygulanmayan son kaynak tedarik tarifelerini destekler.'],
            ['Hesaplanan tutar resmi fatura mıdır?', 'Hayır. Sonuç güncel sistem tarifeleriyle oluşturulan bilgilendirici bir hesaplamadır; tedarik şirketinin düzenlediği resmi faturanın yerine geçmez.']
          ].map(([name, text]) => ({
            '@type': 'Question',
            name,
            acceptedAnswer: { '@type': 'Answer', text }
          }))
        }
      ]
    });
    document.getElementById(structuredData.id)?.remove();
    document.head.appendChild(structuredData);

    return () => {
      document.title = previousTitle;
      Object.entries(previousMeta).forEach(([name, content]) => {
        const element = document.head.querySelector(`meta[name="${name}"]`);
        if (content === null) element?.remove();
        else element?.setAttribute('content', content);
      });
      if (previousCanonical === null) canonicalElement.remove();
      else canonicalElement.setAttribute('href', previousCanonical);
      socialMeta.forEach(({ element, previous, created }) => {
        if (created) element.remove();
        else element.setAttribute('content', previous);
      });
      structuredData.remove();
    };
  }, []);

  return null;
};

const SummaryCard = ({ icon, label, value, accent }) => (
  <Card className={`bill-summary-card ${accent || ''}`} variant="outlined">
    <CardContent>
      <Box className="bill-summary-icon">{icon}</Box>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5">{value}</Typography>
    </CardContent>
  </Card>
);

const BillResult = ({ result, enteredConsumption, tariff, subsidized, startDate, endDate, onReset }) => {
  const mainResult = result?.subMeters?.find((meter) => meter.subMeterId === -1) || result?.subMeters?.[0] || result;
  const buckets = mainResult?.bucketConsumptions || [];
  const taxes = mainResult?.taxesByTypeId || result?.taxesByTypeId || {};

  return (
    <section className="bill-result" aria-labelledby="bill-result-title">
      <Paper className="bill-result-hero" elevation={0}>
        <Box>
          <Chip icon={<CheckCircleOutlineIcon />} label="Hesaplama tamamlandı" color="success" />
          <Typography id="bill-result-title" component="h2" variant="h4">Tahmini elektrik faturanız</Typography>
          <Typography color="text.secondary">
            {dayjs(startDate).format('DD.MM.YYYY')} – {dayjs(endDate).format('DD.MM.YYYY')} · {tariff?.name}
          </Typography>
        </Box>
        <Box className="bill-total-amount">
          <Typography variant="body2">Ödenecek tahmini tutar</Typography>
          <Typography variant="h3">{money(result.totalCharge)} TL</Typography>
          <Chip size="small" label={subsidized ? 'Sübvansiyonlu tarife' : 'Sübvansiyonsuz / SKTT'} />
        </Box>
      </Paper>

      <Grid container spacing={2} sx={{ my: 1 }}>
        <Grid item xs={12} md={4}>
          <SummaryCard icon={<ElectricBoltOutlinedIcon />} label="Toplam tüketim" value={`${quantity(enteredConsumption)} kWh`} accent="energy" />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard icon={<ReceiptLongOutlinedIcon />} label="Enerji ve dağıtım" value={`${money(result.totalConsumptionCharge)} TL`} />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard icon={<CalculateOutlinedIcon />} label="Vergiler dahil toplam" value={`${money(result.totalCharge)} TL`} accent="total" />
        </Grid>
      </Grid>

      <Paper className="bill-detail-panel" variant="outlined">
        <Box className="bill-section-heading">
          <Box>
            <Typography component="h3" variant="h5">Tarife ve kademe ayrıntıları</Typography>
            <Typography color="text.secondary">Her tüketim diliminin hangi birim fiyatla hesaplandığını inceleyin.</Typography>
          </Box>
        </Box>

        {buckets.map((bucket) => {
          const bucketCharge = (bucket.thresholdConsumptions || []).reduce((total, item) => total + Number(item.charge || 0), 0);
          return (
            <Box className="bill-bucket" key={bucket.bucketId || bucket.bucketName}>
              <Box className="bill-bucket-title">
                <Typography component="h4" variant="h6">{bucket.bucketName?.replace('Kovası', 'Bedeli')}</Typography>
                <Typography fontWeight={700}>{money(bucketCharge)} TL</Typography>
              </Box>
              <TableContainer>
                <Table size="small" aria-label={`${bucket.bucketName} hesaplama ayrıntıları`}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Kademe</TableCell>
                      <TableCell align="right">Tüketim</TableCell>
                      <TableCell align="right">Birim fiyat</TableCell>
                      <TableCell align="right">Tutar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(bucket.thresholdConsumptions || []).map((threshold, index) => (
                      <TableRow key={threshold.thresholdId || index}>
                        <TableCell>{(bucket.thresholdConsumptions || []).length > 1 ? `${index + 1}. kademe` : 'Tek kademe'}</TableCell>
                        <TableCell align="right">{quantity(threshold.consumption)} kWh</TableCell>
                        <TableCell align="right">{quantity(threshold.unitPrice, 6)} TL/kWh</TableCell>
                        <TableCell align="right"><strong>{money(threshold.charge)} TL</strong></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {Number(bucket.taxesTotalAmount || 0) > 0 && (
                <Typography className="bill-inline-tax" variant="body2">
                  Bu kaleme bağlı vergiler: <strong>{money(bucket.taxesTotalAmount)} TL</strong>
                </Typography>
              )}
            </Box>
          );
        })}

        <Divider sx={{ my: 2 }} />
        <Box className="bill-totals">
          <Box><span>Enerji, dağıtım ve diğer tüketim bedelleri</span><strong>{money(result.totalConsumptionCharge)} TL</strong></Box>
          {Object.entries(taxes).map(([id, amount]) => (
            <Box key={id}><span>{taxLabel(id)}</span><strong>{money(amount)} TL</strong></Box>
          ))}
          {result.actualInvoiceAmountDiff != null && Number(result.actualInvoiceAmountDiff) !== 0 && (
            <Box><span>Fatura tutarı farkı</span><strong>{money(result.actualInvoiceAmountDiff)} TL</strong></Box>
          )}
          <Box className="bill-grand-total"><span>Toplam fatura</span><strong>{money(result.totalCharge)} TL</strong></Box>
        </Box>
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        Bu sonuç, seçtiğiniz dönem ve sistemde tanımlı güncel tarife verileri kullanılarak hazırlanmış bilgilendirici bir hesaplamadır. Resmî fatura yerine geçmez; yuvarlama, sayaç okuma ve tedarik şirketine özgü uygulamalar küçük farklar oluşturabilir.
      </Alert>

      <Button startIcon={<RestartAltIcon />} variant="contained" size="large" onClick={onReset} sx={{ mt: 3 }}>
        Temizle ve yeniden hesapla
      </Button>
    </section>
  );
};

const ElectricityBillCalculatorPage = () => {
  const [tariffs, setTariffs] = useState([]);
  const [tariffsLoading, setTariffsLoading] = useState(true);
  const [tariffLoadError, setTariffLoadError] = useState('');
  const [tariffId, setTariffId] = useState('');
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [indexTypes, setIndexTypes] = useState([]);
  const [indexTypesLoading, setIndexTypesLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [subsidized, setSubsidized] = useState(true);
  const [consumptions, setConsumptions] = useState({});
  const [errors, setErrors] = useState({});
  const [calculating, setCalculating] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    let active = true;
    TariffService.getTariffs()
      .then((response) => { if (active) setTariffs(response.data); })
      .catch(() => { if (active) setTariffLoadError('Tarifeler yüklenemedi. Lütfen sayfayı yenileyin.'); })
      .finally(() => { if (active) setTariffsLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!tariffId) {
      setIndexTypes([]);
      return undefined;
    }
    let active = true;
    setIndexTypesLoading(true);
    setConsumptions({});
    setResult(null);
    TariffService.getInvoiceIndexTypesByTariffId(tariffId)
      .then((response) => { if (active) setIndexTypes(response.data || []); })
      .catch(() => { if (active) setRequestError('Seçilen tarifeye ait tüketim alanları yüklenemedi.'); })
      .finally(() => { if (active) setIndexTypesLoading(false); });
    return () => { active = false; };
  }, [tariffId]);

  const handleTariffChange = useCallback((tariff) => {
    setSelectedTariff(tariff || null);
    setTariffId(tariff?.tariffId || '');
    setErrors((current) => ({ ...current, tariffId: false }));
  }, []);

  const enteredConsumption = useMemo(() => indexTypes
    .filter((item) => !['P', 'D'].includes(item.indexType))
    .reduce((total, item) => total + Number(consumptions[item.indexType] || 0), 0), [consumptions, indexTypes]);

  const validate = () => {
    const nextErrors = {
      tariffId: !tariffId,
      startDate: !startDate,
      endDate: !endDate || (startDate && endDate && !dayjs(endDate).isAfter(dayjs(startDate))),
      consumption: !indexTypes.some((item) => Number(consumptions[item.indexType] || 0) > 0)
    };
    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleCalculate = async (event) => {
    event.preventDefault();
    setRequestError('');
    if (!validate()) return;

    const request = {
      startDate: dayjs(startDate).format('YYYY-MM-DD'),
      endDate: dayjs(endDate).format('YYYY-MM-DD'),
      tariffId,
      subsidized,
      contractPowerChargeSharing: indexTypes.some((item) => item.indexType === 'P') ? 'MAIN_METER_ONLY' : null,
      exceededPowerChargeSharing: indexTypes.some((item) => item.indexType === 'D') ? 'MAIN_METER_ONLY' : null,
      mainMeterConsumptions: indexTypes
        .map((item) => ({ indexType: item.indexType, consumption: Number(consumptions[item.indexType] || 0) }))
        .filter((item) => item.consumption > 0),
      subMeterConsumptions: []
    };

    setCalculating(true);
    try {
      const response = await RatingService.rating(request);
      setResult(response.data);
      window.requestAnimationFrame(() => document.getElementById('bill-result-title')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    } catch (error) {
      setRequestError(getErrorMessage(error));
    } finally {
      setCalculating(false);
    }
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setConsumptions({});
    setResult(null);
    setRequestError('');
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="electricity-calculator-page">
      <SeoHead />
      <Container maxWidth="lg" component="main">
        <header className="electricity-calculator-hero">
          <Chip label="Ücretsiz · Güncel tarife verileri" className="hero-chip" />
          <Typography component="h1" variant="h2">Elektrik Faturası Hesaplama</Typography>
          <Typography className="hero-lead">
            Tüketiminizi ve tarifenizi seçin; elektrik faturanızı enerji bedeli, dağıtım bedeli, kademeler ve vergiler dahil ayrıntılı biçimde hesaplayın.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} className="hero-feature-list">
            <Chip label="Tüm tarife grupları" />
            <Chip label="Sübvansiyonlu ve SKTT" />
            <Chip label="Tek ve çok zamanlı" />
            <Chip label="Ayrıntılı tutar dökümü" />
          </Stack>
        </header>

        {!result ? (
          <Paper component="form" onSubmit={handleCalculate} className="bill-calculator-form" elevation={0} noValidate>
            <Box className="bill-form-heading">
              <Box className="bill-form-number">1</Box>
              <Box>
                <Typography component="h2" variant="h5">Fatura ve tarife bilgileri</Typography>
                <Typography color="text.secondary">Faturanızdaki abone ve okuma dönemi bilgilerini seçin.</Typography>
              </Box>
            </Box>

            {tariffLoadError && <Alert severity="error" sx={{ mb: 2 }}>{tariffLoadError}</Alert>}
            {tariffsLoading ? <Box className="bill-loading"><CircularProgress /><span>Tarifeler yükleniyor…</span></Box> : (
              <TariffSelector
                tariffs={tariffs}
                value={tariffId}
                onChange={handleTariffChange}
                error={Boolean(errors.tariffId)}
                helperText={errors.tariffId ? 'Tarife seçimi zorunludur.' : ''}
                disabled={calculating}
              />
            )}

            <Box className="subsidy-choice">
              <FormControlLabel
                control={<Switch checked={subsidized} onChange={(event) => setSubsidized(event.target.checked)} />}
                label={subsidized ? 'Sübvansiyon uygulanan tarife' : 'Sübvansiyon uygulanmayan tarife (SKTT)'}
              />
              <Typography variant="body2" color="text.secondary">
                Yıllık tüketim sınırını aşan veya Son Kaynak Tedarik Tarifesi kapsamındaki bir tüketiciyseniz seçimi kapatın.
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required fullWidth type="date" label="İlk okuma tarihi" value={startDate}
                  onChange={(event) => setStartDate(event.target.value)} InputLabelProps={{ shrink: true }}
                  error={Boolean(errors.startDate)} helperText={errors.startDate ? 'İlk okuma tarihini seçin.' : 'Fatura döneminin başlangıcı'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required fullWidth type="date" label="Son okuma tarihi" value={endDate}
                  onChange={(event) => setEndDate(event.target.value)} InputLabelProps={{ shrink: true }}
                  error={Boolean(errors.endDate)} helperText={errors.endDate ? 'Son tarih ilk tarihten sonra olmalıdır.' : 'Fatura döneminin bitişi'}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />
            <Box className="bill-form-heading">
              <Box className="bill-form-number">2</Box>
              <Box>
                <Typography component="h2" variant="h5">Tüketim bilgileri</Typography>
                <Typography color="text.secondary">Seçilen tarifeye göre gereken tüketim değerlerini kWh cinsinden girin.</Typography>
              </Box>
            </Box>

            {indexTypesLoading && <Box className="bill-loading"><CircularProgress size={24} /><span>Tüketim alanları hazırlanıyor…</span></Box>}
            {!tariffId && <Alert severity="info">Tüketim alanlarını görmek için önce tarifenizi seçin.</Alert>}
            {tariffId && !indexTypesLoading && (
              <Grid container spacing={2}>
                {indexTypes.map((item) => (
                  <Grid item xs={12} sm={indexTypes.length > 1 ? 6 : 12} key={item.indexType}>
                    <DecimalInput
                      id={`bill-consumption-${item.indexType}`}
                      required
                      label={item.indexName}
                      value={consumptions[item.indexType] || ''}
                      onChange={(value) => {
                        setConsumptions((current) => ({ ...current, [item.indexType]: value }));
                        setErrors((current) => ({ ...current, consumption: false }));
                      }}
                      decimalSeparator="," maxDecimalDigits={3} unit={item.unit}
                      error={Boolean(errors.consumption)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            {errors.consumption && <Typography color="error" variant="caption">En az bir tüketim değeri girmelisiniz.</Typography>}
            {requestError && <Alert severity="error" sx={{ mt: 2 }}>{requestError}</Alert>}

            <Button
              type="submit" variant="contained" size="large" startIcon={calculating ? <CircularProgress size={18} color="inherit" /> : <CalculateOutlinedIcon />}
              disabled={calculating || tariffsLoading || indexTypesLoading} className="bill-calculate-button"
            >
              {calculating ? 'Fatura hesaplanıyor…' : 'Elektrik faturasını hesapla'}
            </Button>
          </Paper>
        ) : (
          <BillResult
            result={result} enteredConsumption={enteredConsumption} tariff={selectedTariff} subsidized={subsidized}
            startDate={startDate} endDate={endDate} onReset={handleReset}
          />
        )}

        <section className="bill-seo-content" aria-labelledby="calculator-about-title">
          <Typography id="calculator-about-title" component="h2" variant="h4">Elektrik faturası hesaplama aracı neleri dikkate alır?</Typography>
          <Typography>
            Elektrik faturası yalnızca tüketilen kWh ile tek bir fiyatın çarpımından oluşmaz. Abone grubu, tek veya çok zamanlı tarife, tek ya da çift terimli yapı, gerilim seviyesi, okuma döneminin gün sayısı, tüketim kademeleri, aktif enerji bedeli, dağıtım bedeli ve vergiler toplam tutarı etkiler. Bu araç, seçtiğiniz tarifeye ait güncel hesaplama kurallarını rating sistemi üzerinden uygular ve sonucu kalem kalem gösterir.
          </Typography>
          <Grid container spacing={2} sx={{ my: 2 }}>
            {[
              ['Güncel tarife desteği', 'Mesken, tarımsal faaliyetler, sanayi, kamu ve özel hizmetler ile sistemde bulunan diğer tarife grupları desteklenir. Tarife seçimi sonucunda gereken tüketim alanları otomatik hazırlanır.'],
              ['Sübvansiyonlu ve sübvansiyonsuz hesaplama', 'Ulusal tarife kapsamında sübvansiyon uygulanan tüketimler ile Son Kaynak Tedarik Tarifesi kapsamındaki sübvansiyonsuz tüketimler ayrı hesaplama mantıklarıyla değerlendirilebilir.'],
              ['Kademe ve zaman dilimi ayrıntıları', 'Tek zamanlı kademeli tarifelerde düşük ve yüksek kademe; çok zamanlı tarifelerde gündüz, puant ve gece tüketimleri ilgili birim fiyatlarla ayrı ayrı hesaplanır.'],
              ['Şeffaf fatura dökümü', 'Sonuçta tüketim miktarı, her kademenin birim fiyatı, aktif enerji ve dağıtım tutarları, BTV, KDV ve toplam fatura aynı ekranda sunulur.']
            ].map(([title, text]) => (
              <Grid item xs={12} md={6} key={title}>
                <Card variant="outlined" className="bill-capability-card"><CardContent><Typography component="h3" variant="h6">{title}</Typography><Typography color="text.secondary">{text}</Typography></CardContent></Card>
              </Grid>
            ))}
          </Grid>

          <Typography component="h2" variant="h4">Hesaplama nasıl yapılır?</Typography>
          <ol className="bill-how-to-list">
            <li><strong>Tarifenizi seçin:</strong> Faturanızdaki abone grubu, zaman tipi, terim tipi ve gerilim seviyesini kullanın.</li>
            <li><strong>Sübvansiyon durumunu belirtin:</strong> Normal ulusal tarife için açık, SKTT kapsamındaysanız kapalı seçin.</li>
            <li><strong>Okuma dönemini girin:</strong> Kademe sınırları dönem gün sayısına bağlı olabildiğinden gerçek ilk ve son okuma tarihlerini kullanın.</li>
            <li><strong>Tüketimleri ekleyin:</strong> Tek zamanlı tarifede toplam tüketimi; çok zamanlı tarifede gündüz, puant ve gece değerlerini ayrı girin.</li>
            <li><strong>Dökümü inceleyin:</strong> Hesaplama sonrasında kademeleri, birim fiyatları, enerji ve dağıtım bedellerini, vergileri ve genel toplamı kontrol edin.</li>
          </ol>

          <Typography component="h2" variant="h4">Elektrik satış şirketleriyle uyumlu hesaplama yaklaşımı</Typography>
          <Typography>
            Hesaplama motoru, elektrik satış şirketlerinin faturalamada kullandığı tarife bileşenlerini aynı mantıkla ele alır. Tüketimler ilgili fiyat kovalarına ve kademelere dağıtılır; enerji ve dağıtım bedelleri hesaplandıktan sonra vergi kuralları uygulanır. Böylece yalnızca yaklaşık bir “kWh × fiyat” sonucu yerine, faturanın nasıl oluştuğunu açıklayan denetlenebilir bir döküm elde edersiniz. Sistem tarifeleri güncel tutulduğu için hesaplama tarihine ve seçilen tarife yapısına uygun veriler kullanılır.
          </Typography>

          <Typography component="h2" variant="h4">Sık sorulan sorular</Typography>
          <Box className="bill-faq-list">
            <div><Typography component="h3" variant="h6">Hesaplanan tutar neden faturamdan farklı olabilir?</Typography><Typography>Yuvarlama, sayaç okuma tarihleri, tedarik şirketine özgü düzeltmeler, geçmiş dönem farkları ve sonradan yansıtılan bedeller küçük farklılıklar oluşturabilir. Tarih ve tüketim değerlerini faturanızdan aynen girmeniz doğruluğu artırır.</Typography></div>
            <div><Typography component="h3" variant="h6">Sübvansiyonsuz tarife nedir?</Typography><Typography>Belirlenen tüketim sınırlarını aşan bazı tüketiciler Son Kaynak Tedarik Tarifesi kapsamında sübvansiyonsuz fiyatlandırılabilir. Faturanızda SKTT veya son kaynak tedarik bilgisi bulunuyorsa sübvansiyon seçimini kapatabilirsiniz.</Typography></div>
            <div><Typography component="h3" variant="h6">Bu hesaplama resmî fatura yerine geçer mi?</Typography><Typography>Hayır. Araç kontrol ve bilgilendirme amacıyla ayrıntılı bir tahmin oluşturur. Resmî borç ve ödeme bilgisi elektrik tedarik şirketinizin düzenlediği faturadır.</Typography></div>
          </Box>

          <Alert severity="success" icon={<ElectricBoltOutlinedIcon />} className="submeter-callout">
            Aynı aboneliği birden fazla kullanıcı veya süzme sayaçla paylaşıyorsanız, <Link to="/landing">Süzme Sayaç Hesaplama</Link> aracı tüketimleri ve fatura kalemlerini kullanıcılar arasında ayrıntılı biçimde paylaştırabilir.
          </Alert>
        </section>
      </Container>
    </div>
  );
};

export default ElectricityBillCalculatorPage;
